/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { Alert, Dialog, Drawer, TextField, Snackbar } from 'daaas-ui';
import { useMsal } from '@azure/msal-react';
import Icon from '@mdi/react';
import {
  mdiBookOpenPageVariant,
  mdiChevronDown,
  mdiEmail,
  mdiFilePdfBox,
  mdiOpenInNew,
} from '@mdi/js';
import { useAxios } from '../Provider/AxiosProvider';
import AlertDialog from './Dialogs/Alert';
import SuccessDialog from './Dialogs/Success';
import { validEmail } from '../../utils/validateEmail';
import { validRequired } from '../../utils/validateString';
import {
  saveTAndCState,
  loginExpired,
  redirectedFromAuthPage,
  saveRedirectedFromAuthPageState,
} from '../../utils/sessionStorage';
import { handleSignOut } from '../../utils/handleSignInOut';

import { config } from '../../Config/AppConfig';
import TermsAndConditionsBody from '../Layout/TermsAndConditionsBody';
import { LANGUAGE } from '../../constants/global';

const HelpRoot = styled('div')(({ theme }) => ({
  '& .help__details': {
    display: 'block',
  },

  '& .help__new-tab': {
    marginLeft: theme.spacing(1),
  },
}));

export function HelpDrawer({ open = false, closeDrawer, toggleSupportDialog }) {
  const { t } = useTranslation([
    'dialogs',
    'common',
    'aaw',
    'cae',
    'faq',
    'gae',
    'termsAndConditions',
  ]);

  const content = () => (
    <HelpRoot>
      <Accordion defaultExpanded>
        <AccordionSummary
          sx={{ mt: -3 }}
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="support-content"
          id="support-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('helpSupportHeading')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('helpSupportParagraph')}
          </Typography>
          <Button
            className="edge-start"
            variant="text"
            color="primary"
            startIcon={<Icon path={mdiEmail} size={1} />}
            onClick={toggleSupportDialog}
          >
            {t('helpSupportButton')}
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="resources-content"
          id="resources-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('helpTrainingHeading')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('helpTrainingParagraph1')}{' '}
            <Link
              color="inherit"
              href={`${t('dialogs:helpTrainingUrl')}`}
              target="_blank"
            >
              {t('helpTrainingLink')}
            </Link>{' '}
            {t('helpTrainingParagraph2')}
          </Typography>
          <Button
            className="edge-start"
            variant="text"
            color="primary"
            startIcon={<Icon path={mdiFilePdfBox} size={1} />}
            href={`${t('dialogs:helpTrainingButtonHref')}`}
            target="_blank"
          >
            {t('helpTrainingButton')}
            <span className="screen-reader-text">({t('common:newTab')})</span>
            <Icon className="help__new-tab" path={mdiOpenInNew} size={0.75} />
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="faq-content"
          id="faq-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('faq:bannerHeading')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('faq:bannerPara1')}
          </Typography>
          <Typography component="h3" variant="subtitle1">
            <Button
              className="edge-start"
              variant="text"
              color="primary"
              startIcon={<Icon path={mdiBookOpenPageVariant} size={1} />}
              href={`${t('common:/urlPrefix')}${t('common:/faq')}`}
            >
              {t('faq:bannerHeadingGuide')}
            </Button>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="aaw-content"
          id="aaw-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('common:aaw')} <abbr>({t('common:aawAcronym')})</abbr>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('helpAAWParagraph')}
          </Typography>
          <Button
            className="edge-start"
            variant="text"
            color="primary"
            startIcon={<Icon path={mdiBookOpenPageVariant} size={1} />}
            href={`${t('aaw:aawUserGuideHref')}`}
            target="_blank"
          >
            {t('helpAAWButton')}
            <span className="screen-reader-text">({t('common:newTab')})</span>
            <Icon className="help__new-tab" path={mdiOpenInNew} size={0.75} />
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="cae-content"
          id="cae-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('common:cae')} <abbr>({t('common:caeAcronym')})</abbr>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('helpCAEParagraph')}
          </Typography>
          <Button
            className="edge-start"
            variant="text"
            color="primary"
            startIcon={<Icon path={mdiBookOpenPageVariant} size={1} />}
            href={`${t('cae:caeUserGuideHref')}`}
            target="_blank"
          >
            {t('helpCAEButton')}
            <span className="screen-reader-text">({t('common:newTab')})</span>
            <Icon className="help__new-tab" path={mdiOpenInNew} size={0.75} />
          </Button>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<Icon path={mdiChevronDown} size={1} />}
          aria-controls="gae-content"
          id="gae-header"
        >
          <Typography component="h3" variant="subtitle1">
            {t('common:gae')} <abbr>({t('common:gaeAcronym')})</abbr>
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="help__details">
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('helpGAEParagraph')}
          </Typography>
          <Button
            className="edge-start"
            variant="text"
            color="primary"
            startIcon={<Icon path={mdiBookOpenPageVariant} size={1} />}
            // TODO French URL when available
            href={`${t('gae:gaeUserGuideHref')}`}
            target="_blank"
          >
            {t('helpGAEButton')}
            <span className="screen-reader-text">({t('common:newTab')})</span>
            <Icon className="help__new-tab" path={mdiOpenInNew} size={0.75} />
          </Button>
        </AccordionDetails>
      </Accordion>
    </HelpRoot>
  );

  return (
    <Drawer
      sx={{
        '& .MuiDrawer-paper': {
          zIndex: '1500 !important',
        },
      }}
      id="help-drawer"
      open={open}
      title={t('common:help')}
      primaryButton={t('common:close')}
      handlePrimaryClick={closeDrawer}
      toggleDrawer={closeDrawer}
      closeText={t('common:close')}
    >
      {content()}
    </Drawer>
  );
}

HelpDrawer.displayName = 'HelpDrawer';

HelpDrawer.propTypes = {
  open: PropTypes.bool,
  closeDrawer: PropTypes.func.isRequired,
  toggleSupportDialog: PropTypes.func.isRequired,
};

export function SupportDialog({ open = false, toggleDialog }) {
  const axios = useAxios();
  const { t } = useTranslation(['dialogs', 'common']);
  const dialogId = 'support-dialog';
  let supportCategoryAction;
  const [openElement, setOpenElement] = React.useState({
    success: false,
    error: false,
    backdrop: false,
  });

  const initial = {
    numErrors: 0,
    dialogError: '',
    workEmail: {
      value: '',
      error: false,
      helperText: '',
    },
    supportCategory: {
      value: null,
      error: false,
      helperText: '',
    },
    supportOtherCategory: {
      value: '',
      error: false,
      helperText: '',
    },
    supportQuestion: {
      value: '',
      error: false,
      helperText: '',
    },
  };

  const [state, setState] = React.useState({ ...initial });

  const [submitted, setSubmitted] = React.useState(false);

  const toggleSuccessDialog = () => {
    setOpenElement({ ...openElement, success: !openElement.success });
  };

  const closeSnackbar = () => {
    setOpenElement({ ...openElement, error: false });
  };

  const closeBanner = () => {
    setState({ ...state, dialogError: '' });
  };

  const clearForm = () => {
    setState({ ...initial });
  };

  const formValidationError = () => {
    let numErrors = 0;
    const errors = {
      workEmail: {
        error: false,
        helperText: '',
      },
      supportCategory: {
        error: false,
        helperText: '',
      },
      supportOtherCategory: {
        error: false,
        helperText: '',
      },
      supportQuestion: {
        error: false,
        helperText: '',
      },
    };

    const requiredMessage = t('requiredFieldError');
    if (!validRequired(state.workEmail.value)) {
      numErrors += 1;
      errors.workEmail = {
        error: true,
        helperText: requiredMessage,
      };
    } else if (!validEmail(state.workEmail.value)) {
      numErrors += 1;
      errors.workEmail = {
        error: true,
        helperText: t('validEmailAddress'),
      };
    }

    if (!validRequired(state.supportQuestion.value)) {
      numErrors += 1;
      errors.supportQuestion = {
        error: true,
        helperText: requiredMessage,
      };
    }

    if (state.supportCategory.value === null) {
      numErrors += 1;
      errors.supportCategory = {
        error: true,
        helperText: requiredMessage,
      };
    }
    // supportOtherCategory option is only given to user if user has selected "other" option as supportCategory.
    if (
      state.supportCategory.value === 'other' &&
      !validRequired(state.supportOtherCategory.value)
    ) {
      numErrors += 1;
      errors.supportOtherCategory = {
        error: true,
        helperText: requiredMessage,
      };
    }

    setState({
      ...state,
      numErrors,
      workEmail: { ...state.workEmail, ...errors.workEmail },
      supportCategory: { ...state.supportCategory, ...errors.supportCategory },
      supportOtherCategory: {
        ...state.supportOtherCategory,
        ...errors.supportOtherCategory,
      },
      supportQuestion: { ...state.supportQuestion, ...errors.supportQuestion },
    });
    return numErrors > 0;
  };

  const focusFirstError = () => {
    Object.entries(state).some(([key, value]) => {
      if (value.error) {
        document.getElementsByName(key)[0].focus();
        if (key === 'supportCategory') {
          supportCategoryAction.focusVisible();
        }
        return true;
      }
      return false;
    });
  };

  React.useEffect(() => {
    if (state.numErrors) {
      focusFirstError();
    }
  }, [state.numErrors]);

  const handlePrimaryClick = () => {
    if (!formValidationError()) {
      const data = {
        email: state.workEmail.value,
        category: state.supportCategory.value,
        other_category:
          state.supportCategory.value === 'other'
            ? state.supportOtherCategory.value
            : '',
        main_text: state.supportQuestion.value,
      };
      if (submitted) {
        return;
      }
      setSubmitted(true);
      setOpenElement({ ...openElement, backdrop: true });
      axios
        .post(config.api.API_CONTACT_SUPPORT, data)
        .then(() => {
          setOpenElement({ ...openElement, backdrop: false });
          toggleDialog();
          toggleSuccessDialog();
          clearForm();
        })
        .catch((error) => {
          setOpenElement({ ...openElement, backdrop: false });

          let status = 0;
          if ('response' in error) {
            if ('status' in error.response) {
              status = error.response.status;
            } else {
              status = error.response;
            }
          } else {
            status = 500;
            // eslint-disable-next-line no-console
            console.log(`error is ${error.toJson()}`);
          }

          const alertContainer = document.getElementById(`${dialogId}-alerts`);

          switch (status) {
            case 400:
              setState({ ...state, dialogError: t('error400') });
              alertContainer.scrollIntoView();
              break;
            case 401:
              setState({ ...state, dialogError: t('error401') });
              alertContainer.scrollIntoView();
              break;
            case 429:
              setState({ ...state, dialogError: t('error429') });
              alertContainer.scrollIntoView();
              break;
            case 500:
              setState({ ...state, dialogError: t('error500') });
              alertContainer.scrollIntoView();
              break;
            default:
              // eslint-disable-next-line no-console
              console.log(`error is ${error.response.status}`);
          }
        })
        .finally(() => {
          setSubmitted(false);
        });
    } else {
      setOpenElement({ ...openElement, error: true });
      focusFirstError();
    }
  };

  const errorMsg = () => {
    const errors = state.numErrors;
    if (errors === 1) {
      return `${errors} ${t('errorFound')}`;
    }
    return `${errors} ${t('errorsFound')}`;
  };

  const handleInputChange = (event) => {
    const { name } = event.target;
    setState({
      ...state,
      [name]: {
        ...initial[name],
        value: event.target.value,
      },
    });
  };

  const content = () => (
    <Grid container direction="column">
      <Grid
        item
        sx={(theme) => ({
          marginBottom: theme.spacing(3),
          color: alpha(theme.palette.common.black, 0.6),
        })}
      >
        <Typography variant="caption">{t('requiredFields')}</Typography>
      </Grid>
      <Grid item sx={{ mb: 3 }}>
        <TextField
          id="workEmail"
          autoComplete="email"
          type="email"
          label={t('workEmail')}
          variant="outlined"
          fullWidth
          required
          name="workEmail"
          value={state.workEmail.value}
          error={state.workEmail.error}
          helperText={state.workEmail.helperText}
          onChange={handleInputChange}
        />
      </Grid>
      <Divider sx={{ mb: 3 }} />
      <Grid item sx={{ mb: 2 }}>
        <FormControl
          component="fieldset"
          required
          error={state.supportCategory.error}
        >
          <FormLabel component="legend">
            {t('supportCategoryLabel')}
            <span className="screen-reader-text">{t('required')}</span>
          </FormLabel>
          <FormHelperText id="supportCategoryError">
            {state.supportCategory.helperText}
          </FormHelperText>
          <RadioGroup
            id="supportCategory"
            value={state.supportCategory.value}
            name="supportCategory"
            onChange={handleInputChange}
          >
            <FormControlLabel
              value="account"
              control={
                <Radio
                  color="primary"
                  action={(actions) => {
                    supportCategoryAction = actions;
                  }}
                />
              }
              label={t('supportCategoryAccount')}
            />
            <FormControlLabel
              value="aaw"
              control={<Radio color="primary" />}
              label={t('supportCategoryAAW')}
            />
            <FormControlLabel
              value="cae"
              control={<Radio color="primary" />}
              label={t('supportCategoryCAE')}
            />
            <FormControlLabel
              value="gae"
              control={<Radio color="primary" />}
              label={t('supportCategoryGAE')}
            />
            <FormControlLabel
              value="other"
              control={<Radio color="primary" />}
              label={t('other')}
            />
            {state.supportCategory.value === 'other' && (
              <TextField
                sx={{ ml: 4, mb: 1 }}
                id="supportOtherCategory"
                type="text"
                label={t('pleaseSpecify')}
                variant="outlined"
                required
                name="supportOtherCategory"
                value={state.supportOtherCategory.value}
                error={state.supportOtherCategory.error}
                helperText={state.supportOtherCategory.helperText}
                onChange={handleInputChange}
              />
            )}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item>
        <TextField
          id="supportQuestion"
          type="text"
          label={t('supportQuestionLabel')}
          multiline
          minRows="4"
          variant="outlined"
          fullWidth
          required
          name="supportQuestion"
          value={state.supportQuestion.value}
          error={state.supportQuestion.error}
          helperText={state.supportQuestion.helperText}
          onChange={handleInputChange}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Dialog
        sx={{ zIndex: 2600 }}
        id={dialogId}
        open={open}
        title={t('supportHeading')}
        primaryButton={t('submit')}
        secondaryButton={t('cancel')}
        handlePrimaryClick={handlePrimaryClick}
        handleSecondaryClick={() => {
          toggleDialog();
          clearForm();
        }}
        toggleDialog={() => {
          toggleDialog();
          clearForm();
        }}
        closeText={t('common:close')}
        alertBanners={[
          {
            open: Boolean(state.dialogError),
            severity: 'error',
            message: state.dialogError,
            handleClose: closeBanner,
            closeText: t('closeErrorAlert'),
          },
        ]}
        onClose={() => {
          toggleDialog();
          clearForm();
        }}
      >
        {content()}
        {/* Error snackbar */}
        <Snackbar
          message={errorMsg()}
          severity="error"
          open={openElement.error}
          handleClose={closeSnackbar}
        />
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={openElement.backdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
      {/* Success dialog */}
      <SuccessDialog
        open={openElement.success}
        // message={`${t('successMessage')} ${t('successWillContactMessage')}`}
        message={`${t('successMessage')}`}
        toggleDialog={toggleSuccessDialog}
      />
    </>
  );
}

SupportDialog.displayName = 'SupportDialog';

SupportDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
};

export function FeedbackDialog({ open = false, toggleDialog }) {
  const axios = useAxios();
  const { t } = useTranslation(['dialogs', 'common']);
  const dialogId = 'feedback-dialog';
  let feedbackRatingAction;

  const [openElement, setOpenElement] = React.useState({
    success: false,
    error: false,
    backdrop: false,
  });
  const [submitted, setSubmitted] = React.useState(false);

  const initial = {
    numErrors: 0,
    dialogError: '',
    userFeedback: {
      value: '',
      error: false,
      helperText: '',
    },
    feedbackRating: {
      value: null,
      error: false,
      helperText: '',
    },
  };

  const [state, setState] = React.useState({ ...initial });

  const toggleSuccessDialog = () => {
    setOpenElement({ ...openElement, success: !openElement.success });
  };

  const closeSnackbar = () => {
    setOpenElement({ ...openElement, error: false });
  };

  const closeBanner = () => {
    setState({ ...state, dialogError: '' });
  };

  const clearForm = () => {
    setState({ ...initial });
  };

  const formValidationError = () => {
    let numErrors = 0;
    const errors = {
      userFeedback: {
        error: false,
        helperText: '',
      },
      feedbackRating: {
        error: false,
        helperText: '',
      },
    };
    const requiredMsg = t('requiredFieldError');
    if (!validRequired(state.userFeedback.value)) {
      numErrors += 1;
      errors.userFeedback = {
        error: true,
        helperText: requiredMsg,
      };
    }

    if (state.feedbackRating.value === null) {
      numErrors += 1;
      errors.feedbackRating = {
        error: true,
        helperText: requiredMsg,
      };
    }

    setState({
      ...state,
      numErrors,
      userFeedback: { ...state.userFeedback, ...errors.userFeedback },
      feedbackRating: { ...state.feedbackRating, ...errors.feedbackRating },
    });

    return numErrors > 0;
  };

  const focusFirstError = () => {
    Object.entries(state).some(([key, value]) => {
      if (value.error) {
        document.getElementsByName(key)[0].focus();
        if (key === 'feedbackRating') {
          feedbackRatingAction.focusVisible();
        }
        return true;
      }
      return false;
    });
  };

  React.useEffect(() => {
    if (state.numErrors) {
      focusFirstError();
    }
  }, [state.numErrors]);

  // should fire up when submit button on FeedbackDialog is clicked
  const handlePrimaryClick = () => {
    if (!formValidationError()) {
      const data = {
        experience: state.feedbackRating.value,
        main_text: state.userFeedback.value,
      };
      if (submitted) {
        return;
      }
      setSubmitted(true);
      setOpenElement({ ...openElement, backdrop: true });
      axios
        .post(config.api.API_PROVIDE_FEEDBACK, data)
        .then(() => {
          setOpenElement({ ...openElement, backdrop: false });
          toggleDialog();
          toggleSuccessDialog();
          setState({ ...initial });
        })
        .catch((error) => {
          setOpenElement({ ...openElement, backdrop: false });

          let status = 0;
          if ('response' in error) {
            if ('status' in error.response) {
              status = error.response.status;
            } else {
              status = error.response;
            }
          } else {
            status = 500;
            // eslint-disable-next-line no-console
            console.log(`error is ${error.toJson()}`);
          }

          const alertContainer = document.getElementById(`${dialogId}-alerts`);

          switch (status) {
            case 400:
              setState({ ...state, dialogError: t('error400') });
              alertContainer.scrollIntoView();
              break;
            case 401:
              setState({ ...state, dialogError: t('error401') });
              alertContainer.scrollIntoView();
              break;
            case 429:
              setState({ ...state, dialogError: t('error429') });
              alertContainer.scrollIntoView();
              break;
            case 500:
              setState({ ...state, dialogError: t('error500') });
              alertContainer.scrollIntoView();
              break;
            default:
              // eslint-disable-next-line no-console
              console.log(`error is ${error.response.status}`);
          }
        })
        .finally(() => {
          setSubmitted(false);
        });
    } else {
      setOpenElement({ ...openElement, error: true });
      focusFirstError();
    }
  };

  const errorMsg = () => {
    const errors = state.numErrors;
    if (errors === 1) {
      return `${errors} ${t('errorFound')}`;
    }
    return `${errors} ${t('errorsFound')}`;
  };

  const handleInputChange = (event) => {
    const { name } = event.target;
    setState({
      ...state,
      [name]: {
        ...initial[name],
        value: event.target.value,
      },
    });
  };

  const content = () => (
    <Grid container direction="column">
      <Grid
        item
        sx={(theme) => ({
          marginBottom: theme.spacing(3),
          color: alpha(theme.palette.common.black, 0.6),
        })}
      >
        <Typography variant="caption">{t('requiredFields')}</Typography>
      </Grid>
      <Grid item sx={{ mb: 3 }}>
        <TextField
          id="userFeedback"
          type="text"
          label={t('feedbackQuestionLabel')}
          multiline
          minRows="4"
          name="userFeedback"
          variant="outlined"
          fullWidth
          required
          onChange={handleInputChange}
          value={state.userFeedback.value}
          error={state.userFeedback.error}
          helperText={state.userFeedback.helperText}
        />
      </Grid>
      <Grid item>
        <FormControl
          component="fieldset"
          required
          error={state.feedbackRating.error}
          aria-describedby="feedbackRatingError"
        >
          <FormLabel component="legend">
            {t('feedbackRatingLabel')}
            <span className="screen-reader-text">{t('required')}</span>
          </FormLabel>
          <FormHelperText id="feedbackRatingError">
            {state.feedbackRating.helperText}
          </FormHelperText>
          <RadioGroup
            id="feedbackRating"
            value={state.feedbackRating.value}
            name="feedbackRating"
            onChange={handleInputChange}
          >
            <FormControlLabel
              value="Positive"
              control={
                <Radio
                  color="primary"
                  action={(actions) => {
                    feedbackRatingAction = actions;
                  }}
                />
              }
              label={t('feedbackRatingPositive')}
            />
            <FormControlLabel
              value="Neutral"
              control={<Radio color="primary" />}
              label={t('feedbackRatingNeutral')}
            />
            <FormControlLabel
              value="Negative"
              control={<Radio color="primary" />}
              label={t('feedbackRatingNegative')}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Dialog
        id={dialogId}
        open={open}
        title={t('common:feedback')}
        primaryButton={t('submit')}
        secondaryButton={t('cancel')}
        handlePrimaryClick={handlePrimaryClick}
        handleSecondaryClick={() => {
          toggleDialog();
          clearForm();
        }}
        toggleDialog={() => {
          toggleDialog();
          clearForm();
        }}
        closeText={t('common:close')}
        onClose={() => {
          toggleDialog();
          clearForm();
        }}
        alertBanners={[
          {
            open: Boolean(state.dialogError),
            severity: 'error',
            message: state.dialogError,
            handleClose: closeBanner,
            closeText: t('closeErrorAlert'),
          },
        ]}
      >
        {content()}
        {/* Error snackbar */}
        <Snackbar
          message={errorMsg()}
          severity="error"
          open={openElement.error}
          handleClose={closeSnackbar}
        />
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={openElement.backdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
      {/* Success dialog */}
      <SuccessDialog
        open={openElement.success}
        message={`${t('successMessage')}`}
        toggleDialog={toggleSuccessDialog}
      />
    </>
  );
}

FeedbackDialog.displayName = 'FeedbackDialog';

FeedbackDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
};

export function ProfileDialog({ open = false, toggleDialog }) {
  const { t } = useTranslation();
  const { instance } = useMsal();

  const menuContent = () => (
    <Grid container direction="column">
      <Grid
        item
        sx={(theme) => ({
          marginBottom: theme.spacing(3),
        })}
      >
        <Typography variant="subtitle2" component="h3">
          {t('contactEmail')}
        </Typography>
        <Typography variant="body1" component="h4">
          {' '}
          {instance.getActiveAccount()
            ? instance.getActiveAccount().username
            : ''}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle2" component="h3">
          {t('azureAccount')}
        </Typography>
        <Typography variant="body1" component="h4">
          {' '}
          {instance.getActiveAccount()
            ? instance.getActiveAccount().username.split('@')[0]
            : ''}
        </Typography>
      </Grid>
    </Grid>
  );
  return (
    <Dialog
      id="profile-dialog"
      open={open}
      title={t('common:profile')}
      primaryButton={t('common:close')}
      handlePrimaryClick={toggleDialog}
      toggleDialog={toggleDialog}
      closeText={t('common:close')}
    >
      {menuContent()}
    </Dialog>
  );
}

ProfileDialog.displayName = 'ProfileDialog';

ProfileDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
};

export function ForgotPasswordDialog({ open = false, toggleDialog, vmName }) {
  const axios = useAxios(true);
  const { t, i18n } = useTranslation(['dialogs', 'common']);
  const dialogId = 'forgotPassword-dialog';
  const title = `${t('dialogs:forgotPasswordHeading')}: ${vmName}`;
  const [openElement, setOpenElement] = React.useState({
    success: false,
    backdrop: false,
  });

  const initial = {
    dialogError: '',
  };

  const [state, setState] = React.useState({ ...initial });

  const [submitted, setSubmitted] = React.useState(false);

  const toggleSuccessDialog = () => {
    setOpenElement({ ...openElement, success: !openElement.success });
  };

  const closeBanner = () => {
    setState({ ...state, dialogError: '' });
  };

  const clearForm = () => {
    setState({ ...initial });
  };

  const { instance } = useMsal();

  const handlePrimaryClick = () => {
    const data = {
      userName: instance.getActiveAccount().username,
      vmName,
      language: i18n.language,
    };
    if (submitted) {
      return;
    }
    setSubmitted(true);
    setOpenElement({ ...openElement, backdrop: true });
    axios
      .post(config.api.API_PASSWORD_RESET, data)
      .then(() => {
        setOpenElement({ ...openElement, backdrop: false });
        toggleDialog();
        toggleSuccessDialog();
        clearForm();
      })
      .catch((error) => {
        setOpenElement({ ...openElement, backdrop: false });

        let status = 0;
        if ('response' in error) {
          if ('status' in error.response) {
            status = error.response.status;
          } else {
            status = error.response;
          }
        } else {
          status = 500;
          // eslint-disable-next-line no-console
          console.log(`error is ${error.toJson()}`);
        }

        const alertContainer = document.getElementById(`${dialogId}-alerts`);

        switch (status) {
          case 400:
            setState({ ...state, dialogError: t('error400') });
            alertContainer.scrollIntoView();
            break;
          case 401:
            setState({ ...state, dialogError: t('error401') });
            alertContainer.scrollIntoView();
            break;
          case 429:
            setState({ ...state, dialogError: t('error429') });
            alertContainer.scrollIntoView();
            break;
          case 500:
            setState({ ...state, dialogError: t('error500') });
            alertContainer.scrollIntoView();
            break;
          default:
            // eslint-disable-next-line no-console
            console.log(`error is ${error.response.status}`);
        }
      })
      .finally(() => {
        setSubmitted(false);
      });
  };

  const content = () => (
    <Grid container direction="column">
      <Grid item>
        <FormControl component="fieldset">
          <Typography sx={{ mb: 1 }}>
            {t('forgotPasswordFirstParagraph')}
          </Typography>
          <Typography>{t('forgotPasswordSecondParagraph')}</Typography>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Dialog
        id={dialogId}
        open={open}
        title={title}
        primaryButton={t('submit')}
        secondaryButton={t('cancel')}
        handlePrimaryClick={handlePrimaryClick}
        handleSecondaryClick={() => {
          toggleDialog();
          clearForm();
        }}
        toggleDialog={() => {
          toggleDialog();
          clearForm();
        }}
        closeText={t('common:close')}
        alertBanners={[
          {
            open: Boolean(state.dialogError),
            severity: 'error',
            message: state.dialogError,
            handleClose: closeBanner,
            closeText: t('closeErrorAlert'),
          },
        ]}
        onClose={() => {
          toggleDialog();
          clearForm();
        }}
      >
        {content()}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={openElement.backdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
      {/* Success dialog */}
      <SuccessDialog
        open={openElement.success}
        message={`${t('successForgotPasswordMessage')}`}
        toggleDialog={toggleSuccessDialog}
      />
    </>
  );
}

ForgotPasswordDialog.displayName = 'ForgotPasswordDialog';

ForgotPasswordDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
  vmName: PropTypes.string.isRequired,
};

const VmAutoShutdownScheduleRoot = styled('div')(() => ({
  '& .shutdownSelect': {
    minWidth: '130px',
  },
  '& .shutdownSelectHour': {
    minWidth: '115px',
  },
  '& .MuiButtonBase-root.MuiRadio-root.Mui-checked.Mui-disabled svg': {
    color: 'rgba(0,0,0,0.26)',
  },
  '& .noPaddingFieldset': {
    padding: '0px',
  },
  '& .labelDisabled': {
    opacity: 0.4,
  },
}));

export function VmAutoShutdownScheduleDialog({
  open = false,
  toggleDialog,
  successfulSave,
  dtl,
  vmName,
  shutdownEnabled = null,
  shutdownHour,
  shutdownMinute,
  shutdownAmPm,
  timeZone,
  notificationEnabled = null,
  emailAddress,
  notificationMinutesBeforeShutdown,
}) {
  const axios = useAxios(true);
  const { t, i18n } = useTranslation(['dialogs', 'common']);
  const dialogId = 'vm-auto-shutdown-schedule-dialog';
  let shutdownEnabledAction;
  let notificationEnabledAction;

  const title = `${t('dialogs:vmAutoShutdownSettingsFor')}: ${vmName}`;

  const [openElement, setOpenElement] = React.useState({
    success: false,
    error: false,
    backdrop: false,
    noChanges: false,
  });
  const [saved, setSaved] = React.useState(false);

  const initial = {
    numErrors: 0,
    dialogError: '',
    shutdownEnabled: {
      value: null,
      error: false,
      helperText: '',
    },
    shutdownHour: {
      value: '',
      error: false,
      helperText: '',
    },
    shutdownMinute: {
      value: '',
      error: false,
      helperText: '',
    },
    shutdownAmPm: {
      value: '',
      error: false,
      helperText: '',
    },
    timeZone: {
      value: '',
      error: false,
      helperText: '',
    },
    notificationEnabled: {
      value: null,
      error: false,
      helperText: '',
    },
    emailAddress: {
      value: '',
      error: false,
      helperText: '',
    },
    notificationMinutesBeforeShutdown: {
      value: '',
      error: false,
      helperText: '',
    },
  };

  const [state, setState] = React.useState({
    ...initial,
  });

  React.useEffect(() => {
    setState({
      ...state,
      shutdownEnabled: {
        ...initial.shutdownEnabled,
        value: shutdownEnabled,
      },
      shutdownHour: {
        ...initial.shutdownHour,
        value: shutdownHour,
      },
      shutdownMinute: {
        ...initial.shutdownMinute,
        value: shutdownMinute,
      },
      shutdownAmPm: {
        ...initial.shutdownAmPm,
        value: shutdownAmPm,
      },
      timeZone: {
        ...initial.timeZone,
        value: timeZone,
      },
      notificationEnabled: {
        ...initial.notificationEnabled,
        value: notificationEnabled,
      },
      emailAddress: {
        ...initial.emailAddress,
        value: emailAddress,
      },
      notificationMinutesBeforeShutdown: {
        ...initial.notificationMinutesBeforeShutdown,
        value: notificationMinutesBeforeShutdown,
      },
    });
  }, [
    shutdownEnabled,
    shutdownHour,
    shutdownMinute,
    shutdownAmPm,
    timeZone,
    notificationEnabled,
    emailAddress,
    notificationMinutesBeforeShutdown,
  ]);

  const changeInForm = () => {
    let result = false;
    if (
      (state.shutdownEnabled.value === null && shutdownEnabled !== null) ||
      (state.shutdownEnabled.value !== null && shutdownEnabled === null) ||
      state.shutdownEnabled.value !== shutdownEnabled ||
      state.shutdownHour.value !== shutdownHour ||
      state.shutdownMinute.value !== shutdownMinute ||
      state.shutdownAmPm.value !== shutdownAmPm ||
      state.timeZone.value !== timeZone ||
      (state.notificationEnabled.value === null &&
        notificationEnabled !== null) ||
      (state.notificationEnabled.value !== null &&
        notificationEnabled === null) ||
      state.notificationEnabled.value !== notificationEnabled ||
      state.emailAddress.value !== emailAddress ||
      state.notificationMinutesBeforeShutdown.value !==
        notificationMinutesBeforeShutdown
    ) {
      result = true;
    }
    return result;
  };

  const toggleSuccessDialog = () => {
    setOpenElement({ ...openElement, success: !openElement.success });
  };

  const toggleNoChangesDialog = () => {
    setOpenElement({ ...openElement, noChanges: !openElement.noChanges });
  };

  const closeSnackbar = () => {
    setOpenElement({ ...openElement, error: false });
  };

  const closeBanner = () => {
    setState({ ...state, dialogError: '' });
  };

  const clearForm = () => {
    setState({ ...initial });
  };

  const objectMap = (obj, fn) =>
    Object.entries(obj).map(([k, v]) => [fn(k, v)]);

  const shutdownHoursEn = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
  ];

  const shutdownHoursFr = [
    '00',
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
  ];

  const shutdownMinutes = ['00', '10', '20', '30', '40', '50'];

  const shutdownAmPmEn = ['AM', 'PM'];

  // "Yukon Standard Time" is also a valid value, but it currently is not in the Azure Portal dropdown, so the time zone shows as empty.  That's the reason it's excluded here.
  // "Canada Central Standard Time" (for Saskatchewan) is also valid but it seems like there are only 6 official time zones in Canada.
  const timeZones = {
    'Pacific Standard Time': `${t('dialogs:timezonePacific')}`,
    'Mountain Standard Time': `${t('dialogs:timezoneMountain')}`,
    'Central Standard Time': `${t('dialogs:timezoneCentral')}`,
    'Eastern Standard Time': `${t('dialogs:timezoneEastern')}`,
    'Atlantic Standard Time': `${t('dialogs:timezoneAtlantic')}`,
    'Newfoundland Standard Time': `${t('dialogs:timezoneNewfoundland')}`,
  };

  const notificationMinutes = [
    '15',
    '30',
    '45',
    '60',
    '75',
    '90',
    '105',
    '120',
  ];

  const formValidationError = () => {
    let numErrors = 0;
    const errors = {
      shutdownEnabled: {
        error: false,
        helperText: '',
      },
      shutdownHour: {
        error: false,
        helperText: '',
      },
      shutdownMinute: {
        error: false,
        helperText: '',
      },
      shutdownAmPm: {
        error: false,
        helperText: '',
      },
      timeZone: {
        error: false,
        helperText: '',
      },
      notificationEnabled: {
        error: false,
        helperText: '',
      },
      emailAddress: {
        error: false,
        helperText: '',
      },
      notificationMinutesBeforeShutdown: {
        error: false,
        helperText: '',
      },
    };
    const requiredMsg = t('dialogs:requiredFieldError');

    if (state.shutdownEnabled.value === null) {
      numErrors += 1;
      errors.shutdownEnabled = {
        error: true,
        helperText: requiredMsg,
      };
    } else if (!['On', 'Off'].includes(state.shutdownEnabled.value)) {
      numErrors += 1;
      errors.shutdownEnabled = {
        error: true,
        helperText: t('dialogs:validValueError'),
      };
    }

    if (state.shutdownEnabled.value === 'On') {
      if (!validRequired(state.shutdownHour.value)) {
        numErrors += 1;
        errors.shutdownHour = {
          error: true,
          helperText: requiredMsg,
        };
      } else {
        // eslint-disable-next-line no-lonely-if
        if (i18n.language === LANGUAGE.EN) {
          if (!shutdownHoursEn.includes(state.shutdownHour.value)) {
            numErrors += 1;
            errors.shutdownHour = {
              error: true,
              helperText: t('dialogs:validValueError'),
            };
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!shutdownHoursFr.includes(state.shutdownHour.value)) {
            numErrors += 1;
            errors.shutdownHour = {
              error: true,
              helperText: t('dialogs:validValueError'),
            };
          }
        }
      }

      if (!validRequired(state.shutdownMinute.value)) {
        numErrors += 1;
        errors.shutdownMinute = {
          error: true,
          helperText: requiredMsg,
        };
      } else if (!shutdownMinutes.includes(state.shutdownMinute.value)) {
        numErrors += 1;
        errors.shutdownMinute = {
          error: true,
          helperText: t('dialogs:validValueError'),
        };
      }

      if (i18n.language === LANGUAGE.EN) {
        if (!validRequired(state.shutdownAmPm.value)) {
          numErrors += 1;
          errors.shutdownAmPm = {
            error: true,
            helperText: requiredMsg,
          };
        } else if (!shutdownAmPmEn.includes(state.shutdownAmPm.value)) {
          numErrors += 1;
          errors.shutdownAmPm = {
            error: true,
            helperText: t('dialogs:validValueError'),
          };
        }
      }

      if (!validRequired(state.timeZone.value)) {
        numErrors += 1;
        errors.timeZone = {
          error: true,
          helperText: requiredMsg,
        };
      } else if (!Object.keys(timeZones).includes(state.timeZone.value)) {
        numErrors += 1;
        errors.timeZone = {
          error: true,
          helperText: t('dialogs:validValueError'),
        };
      }

      if (state.notificationEnabled.value === null) {
        numErrors += 1;
        errors.notificationEnabled = {
          error: true,
          helperText: requiredMsg,
        };
      } else if (!['Yes', 'No'].includes(state.notificationEnabled.value)) {
        numErrors += 1;
        errors.notificationEnabled = {
          error: true,
          helperText: t('dialogs:validValueError'),
        };
      }

      if (state.notificationEnabled.value === 'Yes') {
        if (!validRequired(state.emailAddress.value)) {
          numErrors += 1;
          errors.emailAddress = {
            error: true,
            helperText: requiredMsg,
          };
        } else if (!validEmail(state.emailAddress.value)) {
          numErrors += 1;
          errors.emailAddress = {
            error: true,
            helperText: t('dialogs:validEmailAddress'),
          };
        }

        if (!validRequired(state.notificationMinutesBeforeShutdown.value)) {
          numErrors += 1;
          errors.notificationMinutesBeforeShutdown = {
            error: true,
            helperText: requiredMsg,
          };
        } else if (
          !notificationMinutes.includes(
            state.notificationMinutesBeforeShutdown.value
          )
        ) {
          numErrors += 1;
          errors.notificationMinutesBeforeShutdown = {
            error: true,
            helperText: t('dialogs:validValueError'),
          };
        }
      }
    }

    setState({
      ...state,
      numErrors,
      shutdownEnabled: {
        ...state.shutdownEnabled,
        ...errors.shutdownEnabled,
      },
      shutdownHour: {
        ...state.shutdownHour,
        ...errors.shutdownHour,
      },
      shutdownMinute: {
        ...state.shutdownMinute,
        ...errors.shutdownMinute,
      },
      shutdownAmPm: {
        ...state.shutdownAmPm,
        ...errors.shutdownAmPm,
      },
      timeZone: {
        ...state.timeZone,
        ...errors.timeZone,
      },
      notificationEnabled: {
        ...state.notificationEnabled,
        ...errors.notificationEnabled,
      },
      emailAddress: {
        ...state.emailAddress,
        ...errors.emailAddress,
      },
      notificationMinutesBeforeShutdown: {
        ...state.notificationMinutesBeforeShutdown,
        ...errors.notificationMinutesBeforeShutdown,
      },
    });

    return numErrors > 0;
  };

  const inputRefShutdownHour = React.useRef();
  const inputRefShutdownMinute = React.useRef();
  const inputRefShutdownAmPm = React.useRef();
  const inputRefTimeZone = React.useRef();
  const inputRefNotificationMinutesBeforeShutdown = React.useRef();

  const focusFirstError = () => {
    Object.entries(state).some(([key, value]) => {
      if (value.error) {
        // This line works for all textfields
        document.getElementsByName(key)[0].focus();

        // Special code to send focus to radio/dropdowns
        if (key === 'shutdownEnabled') {
          shutdownEnabledAction.focusVisible();
        } else if (key === 'shutdownHour') {
          inputRefShutdownHour.current.focus();
        } else if (key === 'shutdownMinute') {
          inputRefShutdownMinute.current.focus();
        } else if (key === 'shutdownAmPm') {
          inputRefShutdownAmPm.current.focus();
        } else if (key === 'timeZone') {
          inputRefTimeZone.current.focus();
        } else if (key === 'notificationEnabled') {
          notificationEnabledAction.focusVisible();
        } else if (key === 'notificationMinutesBeforeShutdown') {
          inputRefNotificationMinutesBeforeShutdown.current.focus();
        }

        return true;
      }
      return false;
    });
  };

  React.useEffect(() => {
    if (state.numErrors) {
      focusFirstError();
    }
  }, [state.numErrors]);

  // Fires when Save button is clicked
  const handlePrimaryClick = () => {
    if (changeInForm()) {
      if (!formValidationError()) {
        if (saved) {
          return;
        }
        setSaved(true);
        setOpenElement({ ...openElement, backdrop: true });
        // Calculate time in 24hr format to be sent to backend
        let time24hr = '';
        if (state.shutdownEnabled.value === 'On') {
          if (i18n.language === LANGUAGE.EN) {
            if (state.shutdownAmPm.value === 'AM') {
              if (!['10', '11', '12'].includes(state.shutdownHour.value)) {
                time24hr = `0${state.shutdownHour.value}${state.shutdownMinute.value}`;
              } else if (['10', '11'].includes(state.shutdownHour.value)) {
                time24hr = `${state.shutdownHour.value}${state.shutdownMinute.value}`;
              } else {
                // state.shutdownHour.value === '12'
                time24hr = `00${state.shutdownMinute.value}`;
              }
            } else {
              // state.shutdownAmPm.value === 'PM'
              // eslint-disable-next-line no-lonely-if
              if (state.shutdownHour.value !== '12') {
                time24hr = `${(
                  parseInt(state.shutdownHour.value, 10) + 12
                ).toString()}${state.shutdownMinute.value}`;
              } else {
                // state.shutdownHour.value === '12'
                time24hr = `${state.shutdownHour.value}${state.shutdownMinute.value}`;
              }
            }
          } else {
            // i18n.language === LANGUAGE.FR
            time24hr = `${state.shutdownHour.value}${state.shutdownMinute.value}`;
          }
        }

        const data = {
          dtl,
          vmName,
          shutdownEnabled: state.shutdownEnabled.value,
          shutdownTime: state.shutdownEnabled.value === 'On' ? time24hr : '',
          timeZone:
            state.shutdownEnabled.value === 'On' ? state.timeZone.value : '',
          notificationEnabled:
            state.shutdownEnabled.value === 'On'
              ? state.notificationEnabled.value
              : '',
          emailAddress:
            state.shutdownEnabled.value === 'On' &&
            state.notificationEnabled.value === 'Yes'
              ? state.emailAddress.value
              : '',
          notificationMinutesBeforeShutdown:
            state.shutdownEnabled.value === 'On' &&
            state.notificationEnabled.value === 'Yes'
              ? state.notificationMinutesBeforeShutdown.value
              : '',
          notificationLocale: i18n.language === LANGUAGE.FR ? 'FR' : 'EN',
        };
        axios
          .post(config.api.API_VM_SHUTDOWN_SETTINGS, data)
          .then(() => {
            successfulSave();
            setOpenElement({ ...openElement, backdrop: false });
            toggleDialog();
            toggleSuccessDialog();
            setState({ ...initial });
          })
          .catch((error) => {
            setOpenElement({ ...openElement, backdrop: false });

            let status = 0;
            if ('response' in error) {
              if ('status' in error.response) {
                status = error.response.status;
              } else {
                status = error.response;
              }
            } else {
              status = 500;
              // eslint-disable-next-line no-console
              console.log(`error is ${error.toJson()}`);
            }

            const alertContainer = document.getElementById(
              `${dialogId}-alerts`
            );

            switch (status) {
              case 400:
                setState({ ...state, dialogError: t('dialogs:error400') });
                alertContainer.scrollIntoView();
                break;
              case 401:
                setState({ ...state, dialogError: t('dialogs:error401') });
                alertContainer.scrollIntoView();
                break;
              case 429:
                setState({ ...state, dialogError: t('dialogs:error429') });
                alertContainer.scrollIntoView();
                break;
              case 500:
                setState({ ...state, dialogError: t('dialogs:error500') });
                alertContainer.scrollIntoView();
                break;
              default:
                // eslint-disable-next-line no-console
                console.log(`error is ${error.response.status}`);
            }
          })
          .finally(() => {
            setSaved(false);
          });
      } else {
        setOpenElement({ ...openElement, error: true });
        focusFirstError();
      }
    } else {
      toggleNoChangesDialog();
    }
  };

  const errorMsg = () => {
    const errors = state.numErrors;
    if (errors === 1) {
      return `${errors} ${t('dialogs:errorFound')}`;
    }
    return `${errors} ${t('dialogs:errorsFound')}`;
  };

  const handleInputChange = (event) => {
    const { name } = event.target;
    setState({
      ...state,
      [name]: {
        ...initial[name],
        value: event.target.value,
      },
    });
  };

  const content = () => (
    <VmAutoShutdownScheduleRoot>
      <Grid container direction="column">
        <Grid
          item
          sx={(theme) => ({
            marginBottom: theme.spacing(3),
            color: alpha(theme.palette.common.black, 0.6),
          })}
        >
          <Typography variant="caption">
            {t('dialogs:requiredFields')}
          </Typography>
        </Grid>
        {/* shutdownEnabled */}
        <Grid item sx={{ mb: 3 }}>
          <FormControl
            component="fieldset"
            required
            error={state.shutdownEnabled.error}
            aria-describedby="shutdownEnabledError"
          >
            <FormLabel component="legend">
              {t('dialogs:autoShutdownEnabled')}
              <span className="screen-reader-text">
                {t('dialogs:required')}
              </span>
            </FormLabel>
            <FormHelperText id="shutdownEnabledError">
              {state.shutdownEnabled.helperText}
            </FormHelperText>
            <RadioGroup
              id="shutdownEnabled"
              value={state.shutdownEnabled.value}
              name="shutdownEnabled"
              onChange={handleInputChange}
            >
              <FormControlLabel
                value="On"
                control={
                  <Radio
                    color="primary"
                    action={(actions) => {
                      shutdownEnabledAction = actions;
                    }}
                  />
                }
                label={t('dialogs:on')}
              />
              <FormControlLabel
                value="Off"
                control={<Radio color="primary" />}
                label={t('dialogs:off')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        {/* shutdownHour shutdownMinute shutdownAmPm */}
        <fieldset className="noPaddingFieldset">
          <FormLabel
            component="legend"
            className={
              state.shutdownEnabled.value === null ||
              state.shutdownEnabled.value === 'Off'
                ? 'labelDisabled'
                : ''
            }
          >
            {t('dialogs:vmScheduledShutdown')}
          </FormLabel>
          <Grid item sx={{ mb: 3 }}>
            {/* shutdownHour */}
            <FormControl
              disabled={
                state.shutdownEnabled.value === null ||
                state.shutdownEnabled.value === 'Off'
              }
              required
              error={
                state.shutdownEnabled.value === 'On' && state.shutdownHour.error
              }
              aria-describedby="shutdownHourError"
              className="shutdownSelectHour"
              sx={{ mr: 1, mt: 1 }}
            >
              <InputLabel id="shutdown-hour-label">
                {t('dialogs:vmHour')}
              </InputLabel>
              <Select
                labelId="shutdown-hour-label"
                id="shutdownHour"
                type="text"
                name="shutdownHour"
                variant="outlined"
                value={state.shutdownHour.value}
                onChange={handleInputChange}
                input={
                  <OutlinedInput
                    label={t('dialogs:vmHour')}
                    inputRef={inputRefShutdownHour}
                  />
                }
              >
                {i18n.language === LANGUAGE.FR
                  ? shutdownHoursFr.map((hour) => (
                      <MenuItem
                        key={hour}
                        value={hour}
                        sx={(theme) => ({
                          fontSize: theme.typography.subtitle2.fontSize,
                        })}
                      >
                        {hour}
                      </MenuItem>
                    ))
                  : shutdownHoursEn.map((hour) => (
                      <MenuItem
                        key={hour}
                        value={hour}
                        sx={(theme) => ({
                          fontSize: theme.typography.subtitle2.fontSize,
                        })}
                      >
                        {hour}
                      </MenuItem>
                    ))}
              </Select>
              <FormHelperText id="shutdownHourError">
                {state.shutdownHour.helperText}
              </FormHelperText>
            </FormControl>
            {/* shutdownMinute */}
            <FormControl
              disabled={
                state.shutdownEnabled.value === null ||
                state.shutdownEnabled.value === 'Off'
              }
              required
              error={
                state.shutdownEnabled.value === 'On' &&
                state.shutdownMinute.error
              }
              aria-describedby="shutdownMinuteError"
              className="shutdownSelect"
              sx={{ mr: 1, mt: 1 }}
            >
              <InputLabel id="shutdown-minute-label">
                {t('dialogs:vmMinute')}
              </InputLabel>
              <Select
                labelId="shutdown-minute-label"
                id="shutdownMinute"
                type="text"
                name="shutdownMinute"
                variant="outlined"
                value={state.shutdownMinute.value}
                onChange={handleInputChange}
                input={
                  <OutlinedInput
                    label={t('dialogs:vmMinute')}
                    inputRef={inputRefShutdownMinute}
                  />
                }
              >
                {shutdownMinutes.map((minute) => (
                  <MenuItem
                    key={minute}
                    value={minute}
                    sx={(theme) => ({
                      fontSize: theme.typography.subtitle2.fontSize,
                    })}
                  >
                    {minute}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText id="shutdownMinuteError">
                {state.shutdownMinute.helperText}
              </FormHelperText>
            </FormControl>
            {/* shutdownAmPm */}
            {i18n.language === LANGUAGE.EN ? (
              <FormControl
                disabled={
                  state.shutdownEnabled.value === null ||
                  state.shutdownEnabled.value === 'Off'
                }
                required
                error={
                  state.shutdownEnabled.value === 'On' &&
                  state.shutdownAmPm.error
                }
                aria-describedby="shutdownAmPmError"
                className="shutdownSelect"
                sx={{ mt: 1 }}
              >
                <InputLabel id="shutdown-am-pm-label">
                  {t('dialogs:vmAmPm')}
                </InputLabel>
                <Select
                  labelId="shutdown-am-pm-label"
                  id="shutdownAmPm"
                  type="text"
                  name="shutdownAmPm"
                  variant="outlined"
                  value={state.shutdownAmPm.value}
                  onChange={handleInputChange}
                  input={
                    <OutlinedInput
                      label={t('dialogs:vmAmPm')}
                      inputRef={inputRefShutdownAmPm}
                    />
                  }
                >
                  {shutdownAmPmEn.map((ampm) => (
                    <MenuItem
                      key={ampm}
                      value={ampm}
                      sx={(theme) => ({
                        fontSize: theme.typography.subtitle2.fontSize,
                      })}
                    >
                      {ampm}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText id="shutdownAmPmError">
                  {state.shutdownAmPm.helperText}
                </FormHelperText>
              </FormControl>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
          </Grid>
          {/* timeZone */}
          <Grid item sx={{ mb: 3 }}>
            <FormControl
              disabled={
                state.shutdownEnabled.value === null ||
                state.shutdownEnabled.value === 'Off'
              }
              required
              error={
                state.shutdownEnabled.value === 'On' && state.timeZone.error
              }
              aria-describedby="timeZoneError"
              fullWidth
            >
              <InputLabel id="time-zone-label">
                {t('dialogs:vmTimeZone')}
              </InputLabel>
              <Select
                labelId="time-zone-label"
                id="timeZone"
                type="text"
                name="timeZone"
                variant="outlined"
                value={state.timeZone.value}
                onChange={handleInputChange}
                input={
                  <OutlinedInput
                    label={t('dialogs:vmTimeZone')}
                    inputRef={inputRefTimeZone}
                  />
                }
              >
                {objectMap(timeZones, (k, v) => (
                  <MenuItem
                    value={k}
                    sx={(theme) => ({
                      fontSize: theme.typography.subtitle2.fontSize,
                    })}
                  >
                    {v}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText id="timeZoneError">
                {state.timeZone.helperText}
              </FormHelperText>
            </FormControl>
          </Grid>
        </fieldset>
        {/* notificationEnabled */}
        <Grid item sx={{ mb: 3 }}>
          <FormControl
            disabled={
              state.shutdownEnabled.value === null ||
              state.shutdownEnabled.value === 'Off'
            }
            component="fieldset"
            required
            error={
              state.shutdownEnabled.value === 'On' &&
              state.notificationEnabled.error
            }
            aria-describedby="notificationEnabledError"
          >
            <FormLabel
              component="legend"
              className={
                state.shutdownEnabled.value === null ||
                state.shutdownEnabled.value === 'Off'
                  ? 'labelDisabled'
                  : ''
              }
            >
              {t('dialogs:vmSendNotificationBeforeAutoShutdown')}
              <span className="screen-reader-text">
                {t('dialogs:required')}
              </span>
            </FormLabel>
            <FormHelperText id="notificationEnabledError">
              {state.notificationEnabled.helperText}
            </FormHelperText>
            <RadioGroup
              id="notificationEnabled"
              value={state.notificationEnabled.value}
              name="notificationEnabled"
              onChange={handleInputChange}
            >
              <FormControlLabel
                value="Yes"
                control={
                  <Radio
                    color="primary"
                    action={(actions) => {
                      notificationEnabledAction = actions;
                    }}
                  />
                }
                label={t('dialogs:yes')}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={t('dialogs:no')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        {/* emailAddress */}
        <Grid item sx={{ mb: 3 }}>
          <TextField
            disabled={
              state.shutdownEnabled.value === null ||
              state.shutdownEnabled.value === 'Off' ||
              state.notificationEnabled.value === null ||
              state.notificationEnabled.value === 'No'
            }
            id="emailAddress"
            type="text"
            label={t('dialogs:vmEmailAddressForNotifications')}
            name="emailAddress"
            variant="outlined"
            fullWidth
            required
            value={state.emailAddress.value}
            error={
              state.shutdownEnabled.value === 'On' &&
              state.notificationEnabled.value === 'Yes' &&
              state.emailAddress.error
            }
            helperText={state.emailAddress.helperText}
            onChange={handleInputChange}
          />
        </Grid>
        {/* notificationMinutesBeforeShutdown */}
        <Grid item>
          <FormControl
            disabled={
              state.shutdownEnabled.value === null ||
              state.shutdownEnabled.value === 'Off' ||
              state.notificationEnabled.value === null ||
              state.notificationEnabled.value === 'No'
            }
            required
            error={
              state.shutdownEnabled.value === 'On' &&
              state.notificationEnabled.value === 'Yes' &&
              state.notificationMinutesBeforeShutdown.error
            }
            aria-describedby="notificationMinutesBeforeShutdownError"
            fullWidth
          >
            <InputLabel id="notification-minutes-before-shutdown-label">
              {t('dialogs:vmNotificationMinutesBeforeShutdown')}
            </InputLabel>
            <Select
              labelId="notification-minutes-before-shutdown-label"
              id="notificationMinutesBeforeShutdown"
              type="text"
              name="notificationMinutesBeforeShutdown"
              variant="outlined"
              value={state.notificationMinutesBeforeShutdown.value}
              onChange={handleInputChange}
              input={
                <OutlinedInput
                  label={t('dialogs:vmNotificationMinutesBeforeShutdown')}
                  inputRef={inputRefNotificationMinutesBeforeShutdown}
                />
              }
            >
              {notificationMinutes.map((minute) => (
                <MenuItem
                  key={minute}
                  value={minute}
                  sx={(theme) => ({
                    fontSize: theme.typography.subtitle2.fontSize,
                  })}
                >
                  {minute}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText id="notificationMinutesBeforeShutdownError">
              {state.notificationMinutesBeforeShutdown.helperText}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </VmAutoShutdownScheduleRoot>
  );

  return (
    <>
      <Dialog
        id={dialogId}
        open={open}
        title={title}
        primaryButton={t('dialogs:save')}
        secondaryButton={t('dialogs:cancel')}
        handlePrimaryClick={handlePrimaryClick}
        handleSecondaryClick={() => {
          toggleDialog();
          clearForm();
        }}
        toggleDialog={() => {
          toggleDialog();
          clearForm();
        }}
        successfulSave={() => {
          successfulSave();
        }}
        closeText={t('common:close')}
        onClose={() => {
          toggleDialog();
          clearForm();
        }}
        alertBanners={[
          {
            open: Boolean(state.dialogError),
            severity: 'error',
            message: state.dialogError,
            handleClose: closeBanner,
            closeText: t('dialogs:closeErrorAlert'),
          },
        ]}
      >
        {content()}
        {/* Error snackbar */}
        <Snackbar
          message={errorMsg()}
          severity="error"
          open={openElement.error}
          handleClose={closeSnackbar}
        />
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
          open={openElement.backdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
      {/* Success dialog */}
      <SuccessDialog
        open={openElement.success}
        message={`${t('dialogs:successMessage')}`}
        toggleDialog={toggleSuccessDialog}
      />
      {/* NoChanges dialog */}
      <AlertDialog
        open={openElement.noChanges}
        message={`${t('dialogs:noChangesMessage')}`}
        toggleDialog={toggleNoChangesDialog}
        title={`${t('dialogs:warning')}`}
        severity="warning"
      />
    </>
  );
}

VmAutoShutdownScheduleDialog.displayName = 'VmAutoShutdownScheduleDialog';

VmAutoShutdownScheduleDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
  successfulSave: PropTypes.func.isRequired,
  dtl: PropTypes.string.isRequired,
  vmName: PropTypes.string.isRequired,
  shutdownEnabled: PropTypes.string,
  shutdownHour: PropTypes.string.isRequired,
  shutdownMinute: PropTypes.string.isRequired,
  shutdownAmPm: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  notificationEnabled: PropTypes.string,
  emailAddress: PropTypes.string.isRequired,
  notificationMinutesBeforeShutdown: PropTypes.string.isRequired,
};

export function TermsAndConditionsDialog({
  open = false,
  toggleDialog,
  agreedToTermsAndConditions,
}) {
  const { t } = useTranslation(['dialogs', 'common']);
  const dialogId = 'terms-and-conditions-dialog';
  const title = `${t('termsAndConditions:termsAndConditions')}`;
  const [scrolledToBottom, setScrolledToBottom] = React.useState(false);

  const clearForm = () => {
    setScrolledToBottom(false);
  };

  const handlePrimaryClick = () => {
    toggleDialog();
    clearForm();
    saveTAndCState();
    agreedToTermsAndConditions();
  };

  // Prevent specific keys from trigger page scroll effects while navigating menus
  const handlePreventPageScroll = (e) => {
    if (
      e.key === 'ArrowDown' ||
      e.key === 'ArrowUp' ||
      e.key === 'RightArrow' ||
      e.key === 'LeftArrow' ||
      e.key === 'End' ||
      e.key === 'Home'
    ) {
      e.preventDefault();
    }
  };

  function getScrollPercent(e) {
    const h = e.target;
    const st = 'scrollTop';
    const sh = 'scrollHeight';
    return Math.ceil((h[st] / (h[sh] - h.clientHeight)) * 100);
  }

  const handleScroll = (e) => {
    const bottom = getScrollPercent(e) >= 100;

    if (bottom) {
      setScrolledToBottom(true);
    }
  };

  React.useEffect(() => {
    // Handle scrolling the dialog content with various keys
    function dialogKeyboardNavigation(e) {
      const elem = document.querySelector(
        '#terms-and-conditions-dialog-desc .MuiBox-root'
      );
      handlePreventPageScroll(e);
      if (e.key === 'ArrowDown') {
        elem.scrollBy(0, 30);
      }
      if (e.key === 'ArrowUp') {
        elem.scrollBy(0, -30);
      }
      if (e.key === 'PageUp') {
        const elemHeight = elem.clientHeight;
        elem.scrollBy(0, elemHeight * -1);
      }
      if (e.key === 'PageDown') {
        const elemHeight = elem.clientHeight;
        elem.scrollBy(0, elemHeight);
      }
      if (e.key === 'Home') {
        elem.scrollTo(0, 0);
      }
      if (e.key === 'End') {
        const elemHeight = document.querySelector(
          '#terms-and-conditions-dialog-desc .MuiContainer-root'
        ).scrollHeight;
        elem.scrollTo(0, elemHeight);
      }
    }

    if (open) {
      window.addEventListener('keydown', dialogKeyboardNavigation);
    } else {
      window.removeEventListener('keydown', dialogKeyboardNavigation);
    }
    return () => {
      window.removeEventListener('keydown', dialogKeyboardNavigation);
    };
  }, [open]);

  return (
    <Dialog
      id={dialogId}
      open={open}
      title={title}
      primaryButton={t('termsAndConditions:accept')}
      disablePrimaryButton={!scrolledToBottom}
      handlePrimaryClick={handlePrimaryClick}
      toggleDialog={() => {
        toggleDialog();
        clearForm();
      }}
      agreedToTermsAndConditions={() => {
        agreedToTermsAndConditions();
      }}
      onScroll={handleScroll}
      fullScreen
    >
      <Alert open severity="info" closeable={false}>
        {t('termsAndConditions:termsAndConditionsAlertText')}
      </Alert>
      <TermsAndConditionsBody isUsedInDialog />
    </Dialog>
  );
}

TermsAndConditionsDialog.displayName = 'TermsAndConditionsDialog';

TermsAndConditionsDialog.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func.isRequired,
  agreedToTermsAndConditions: PropTypes.func.isRequired,
};

export function MsalExpiredSessionAlert({
  open = false,
  toggleDialog = () => {},
}) {
  const { t, i18n } = useTranslation(['dialogs']);
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginExpired = loginExpired();

  const [openElement, setOpenElement] = React.useState({
    sessionExpired: false,
  });

  const toggleSessionExpiredDialog = () => {
    setOpenElement({
      ...openElement,
      sessionExpired: !openElement.sessionExpired,
    });
    toggleDialog();
    handleSignOut(instance, i18n.language, navigate, location);
  };

  React.useEffect(() => {
    setOpenElement({ ...openElement, sessionExpired: isLoginExpired });
  }, []);

  return (
    <AlertDialog
      open={openElement.sessionExpired || open}
      message={`${t('dialogs:sessionExpiredMessage')}`}
      toggleDialog={toggleSessionExpiredDialog}
      title={`${t('dialogs:warning')}`}
      severity="warning"
    />
  );
}

MsalExpiredSessionAlert.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
};

export function RedirectedFromAuthPageAlert() {
  const { t } = useTranslation(['dialogs']);
  const isRedirectedFromAuthPage = redirectedFromAuthPage();
  const [openElement, setOpenElement] = React.useState({
    redirected: false,
  });

  const toggleRedirectedDialog = () => {
    setOpenElement({
      ...openElement,
      redirected: !openElement.redirected,
    });
    saveRedirectedFromAuthPageState(false);
  };

  React.useEffect(() => {
    setOpenElement({
      ...openElement,
      redirected: isRedirectedFromAuthPage,
    });
  }, []);

  return (
    <AlertDialog
      open={openElement.redirected}
      message={`${t('dialogs:redirectedMessage')}`}
      toggleDialog={toggleRedirectedDialog}
      title={`${t('dialogs:warning')}`}
      severity="warning"
    />
  );
}

RedirectedFromAuthPageAlert.propTypes = {};
